import React, { useEffect, useRef, useState } from 'react'

import { Box, Stack, Typography, useTheme } from '@mui/material'
import { Button } from '@repo/ui'
import { CircleCheck } from 'lucide-react'

import { DrawerPopup } from 'components/UI/DrawerPopup'
import { Input } from 'components/UI/Input'
import { FieldLabel } from 'components/UI/FieldLabel'
import { OtpInput } from 'components/UI/OtpInput'
import { CodeExpiryNotice } from 'components/auth'
import { OTP_CODE_TTL_SECONDS, useCodeExpiry } from 'hooks/useCodeExpiry'
import { createApiCall } from 'services'
import { useDispatch } from 'store/hooks'
import { getProfile } from 'store/auth/actions'

type Step = 'email' | 'code' | 'success'

type Props = {
  open: boolean
  onClose: () => void
  /** Seeds the first step with the customer's current email. */
  initialEmail?: string | null
}

const CODE_LENGTH = 4

/**
 * VERA-291 — email change, two steps in a single content-height bottom
 * drawer, mirroring `PhoneVerificationDrawer` (same primitive, same spacing/
 * typography, same shared `OtpInput`) so the two read as siblings.
 *
 * Shared by the Notifications page and the Profile page. Replaces the
 * previous single-step, backend-no-op version: `POST /auth/customer/profile`
 * never accepted an `email` field, so saving here previously did nothing but
 * report a fake "confirmation sent" state back to the caller. This now calls
 * the real verification endpoints (`/auth/customer/email/change/request` and
 * `/auth/customer/email/change/confirm`) — the code goes to the NEW address,
 * and the CURRENT address stays active until that code is confirmed here, in
 * the drawer. There is no separate "click a link in your email" step, so
 * unlike the old flow there is nothing left to show a pending/"undo" banner
 * for by the time this drawer reports success — the change has already
 * happened.
 *
 * On confirmed success it dispatches `getProfile()` (matching
 * `PhoneVerificationDrawer`) so both pages' `profile.email` refresh without a
 * full reload.
 */
export const EditEmailDrawer: React.FC<Props> = ({ open, onClose, initialEmail }) => {
  const theme = useTheme()
  const dispatch = useDispatch()

  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  /*
   * VRG-720 — send time of the outstanding code.
   *
   * 10 minutes is this code's real TTL: `RequestEmailChangeUseCase` stores it
   * through `OtpRepository`, whose redis implementation sets
   * `ttl: OTP_TTL_SECONDS`. Same constant as the sign-in code, reached by a
   * different route.
   */
  const [codeIssuedAt, setCodeIssuedAt] = useState<number | null>(null)
  const { expired: codeExpired } = useCodeExpiry(codeIssuedAt, OTP_CODE_TTL_SECONDS)
  const [reference, setReference] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Read through a ref, NOT an effect dependency — see PhoneVerificationDrawer's
  // header comment for the exact bug this avoids (confirming dispatches
  // getProfile(), which changes `initialEmail`, which would re-fire the reset
  // below and wipe the success step while the drawer is still open).
  const initialEmailRef = useRef(initialEmail)
  initialEmailRef.current = initialEmail

  // Reset ONLY on the closed → open transition.
  useEffect(() => {
    if (open) {
      setStep('email')
      setEmail(initialEmailRef.current ?? '')
      setCode('')
      setCodeIssuedAt(null)
      setReference(null)
      setError(null)
      setSubmitting(false)
    }
  }, [open])

  const requestCode = async () => {
    const trimmed = email.trim()
    if (!trimmed || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const { status, data } = await createApiCall({
        method: 'POST',
        url: '/auth/customer/email/change/request',
        data: { email: trimmed },
      })
      if (status === 201 && data?.reference) {
        setReference(data.reference)
        setCode('')
        setCodeIssuedAt(Date.now())
        setStep('code')
      } else if (status === 429) {
        setError("You've requested too many codes. Please wait 15 minutes and try again.")
      } else if (status === 409) {
        setError('This email address is already in use.')
      } else if (status === 400) {
        setError('Enter a valid email address.')
      } else {
        setError(data?.message || "We couldn't send a code to that address. Check it and try again.")
      }
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const confirmCode = async () => {
    if (!reference || code.trim().length !== CODE_LENGTH || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      const { status, data } = await createApiCall({
        method: 'POST',
        url: '/auth/customer/email/change/confirm',
        data: { reference, code: code.trim() },
      })
      if (status === 201 && data?.email_verified) {
        setStep('success')
        dispatch(getProfile())
      } else if (status === 404) {
        setError('That code has expired. Request a new one.')
      } else if (status === 409) {
        setError('This email address is already in use.')
      } else if (status === 400) {
        setError("That code isn't right. Try again.")
      } else {
        setError(data?.message || "We couldn't verify that code. Try again.")
      }
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const title = step === 'email' ? 'Change email' : step === 'code' ? 'Enter verification code' : 'Email updated'

  return (
    <DrawerPopup
      title={title}
      handleOpen={open}
      handleClose={onClose}
      fullScreen={false}
      child={
        step === 'email' ? (
          <Stack spacing={4}>
            <Stack>
              <FieldLabel label='Email address' />
              <Input
                type='email'
                value={email}
                onChange={v => { setEmail(v); setError(null) }}
                placeholder='Email'
                error={Boolean(error)}
              />
            </Stack>

            <Typography variant='body3' sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}>
              We'll send a code to the new address. Your current email stays active until you
              confirm it.
            </Typography>

            {error && (
              <Typography variant='body3' sx={{ color: theme.palette.error.main }}>
                {error}
              </Typography>
            )}
            <Button
              variant='primary'
              fullWidth
              loading={submitting}
              disabled={submitting || !email.trim()}
              onClick={requestCode}
            >
              Send code
            </Button>
          </Stack>
        ) : step === 'code' ? (
          <Stack spacing={4}>
            <Typography variant='body2' sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}>
              Enter the {CODE_LENGTH}-digit code we sent to {email}.
            </Typography>
            <Stack>
              <Typography
                variant='body3'
                sx={{ color: theme.palette.text.primary, mb: 2, textAlign: 'center' }}
              >
                Verification code
              </Typography>
              <OtpInput
                value={code}
                length={CODE_LENGTH}
                error={Boolean(error)}
                disabled={submitting}
                onChange={next => { setCode(next); setError(null) }}
              />
              {error && (
                <Typography
                  variant='body3'
                  sx={{ color: theme.palette.error.main, mt: 2, textAlign: 'center' }}
                >
                  {error}
                </Typography>
              )}
            </Stack>
            {/*
              VERA-291 footer-consistency follow-up: was `variant='text'` — a
              bare text link in a footer, exactly the drift this pass
              eliminates (working-conventions, "text buttons are eliminated
              from footers"). Now `secondary`, grouped with Verify into its
              own tighter 12px stack, mirroring PhoneVerificationDrawer's
              Verify/Resend pairing so the two code-entry drawers read as
              siblings.
            */}
            <CodeExpiryNotice
              issuedAt={codeIssuedAt}
              ttlSeconds={OTP_CODE_TTL_SECONDS}
              resendLabel='Use Resend code below'
            />
            <Stack spacing={1.5}>
              {/*
                VRG-720: Resend takes the fill once the code has expired, and
                Verify steps back without going dead — the countdown predicts
                the server's answer rather than being it. Fuller note in
                OnboardingAuthPanel.
              */}
              <Button
                variant={codeExpired ? 'secondary' : 'primary'}
                fullWidth
                loading={submitting}
                disabled={submitting || code.trim().length !== CODE_LENGTH}
                onClick={confirmCode}
              >
                Verify
              </Button>
              <Button variant={codeExpired ? 'primary' : 'secondary'} fullWidth disabled={submitting} onClick={requestCode}>
                Resend code
              </Button>
            </Stack>
          </Stack>
        ) : (
          <Stack spacing={4} alignItems='center' sx={{ textAlign: 'center', py: 2 }}>
            <Box sx={{ color: (theme.palette as any).brand?.[600] || theme.palette.success.main }}>
              <CircleCheck size={40} />
            </Box>
            <Typography sx={{ fontSize: '16px', fontWeight: 500, color: theme.palette.text.primary }}>
              Email updated
            </Typography>
            <Typography variant='body2' sx={{ color: theme.palette.text.secondary, lineHeight: 1.5 }}>
              You'll use this address to sign in from now on.
            </Typography>
            <Button variant='primary' fullWidth onClick={onClose}>
              Done
            </Button>
          </Stack>
        )
      }
    />
  )
}
